import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import {
  Trophy,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RotateCcw,
} from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * Extract PGN header tag value
 */
function extractTag(pgn, tag) {
  const match = pgn.match(new RegExp(`\\[${tag}\\s+"(.+?)"\\]`));
  return match ? match[1] : '';
}

/**
 * Parse a PGN into move history and starting FEN.
 * Returns { moves: string[], startFen: string } or null on failure.
 */
function parseGame(pgn) {
  try {
    const chess = new Chess();
    chess.loadPgn(pgn);
    const moves = chess.history();
    // Detect custom starting position from PGN FEN header
    const fenTag = extractTag(pgn, 'FEN');
    const startFen = fenTag || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    return { moves, startFen };
  } catch {
    return null;
  }
}

/**
 * Compute FEN at a given move index by replaying moves from startFen.
 */
function computeFen(startFen, moves, moveIndex) {
  const chess = new Chess(startFen);
  for (let i = 0; i < moveIndex; i++) {
    chess.move(moves[i]);
  }
  return chess.fen();
}

/**
 * Get the last move's from/to squares for highlighting.
 */
function getLastMove(startFen, moves, moveIndex) {
  if (moveIndex === 0) return null;
  const chess = new Chess(startFen);
  let lastMove = null;
  for (let i = 0; i < moveIndex; i++) {
    lastMove = chess.move(moves[i]);
  }
  return lastMove ? { from: lastMove.from, to: lastMove.to } : null;
}

const FavouriteGamesRenderer = ({ block, theme = {} }) => {
  const content = block.content || {};
  const games = content.games || [];
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);

  const currentGame = games[currentGameIndex] || null;

  // Parse current game
  const parsed = useMemo(() => {
    if (!currentGame?.pgn) return null;
    return parseGame(currentGame.pgn);
  }, [currentGame?.pgn]);

  const moveHistory = parsed?.moves || [];
  const startFen = parsed?.startFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

  // Reset move index when game changes
  useEffect(() => {
    setCurrentMoveIndex(0);
  }, [currentGameIndex]);

  // Compute current position
  const currentFen = useMemo(
    () => computeFen(startFen, moveHistory, currentMoveIndex),
    [startFen, moveHistory, currentMoveIndex]
  );

  // Last move highlight
  const lastMove = useMemo(
    () => getLastMove(startFen, moveHistory, currentMoveIndex),
    [startFen, moveHistory, currentMoveIndex]
  );

  const safeAccent = styles.accentColor || '#749555';
  const matchTheme = content.matchThemeSquares === true;

  // Move navigation
  const goFirst = useCallback(() => setCurrentMoveIndex(0), []);
  const goPrev = useCallback(() => setCurrentMoveIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setCurrentMoveIndex((i) => Math.min(moveHistory.length, i + 1)),
    [moveHistory.length]
  );
  const goLast = useCallback(
    () => setCurrentMoveIndex(moveHistory.length),
    [moveHistory.length]
  );

  // Game navigation
  const prevGame = useCallback(() => {
    setCurrentGameIndex((i) => Math.max(0, i - 1));
  }, []);
  const nextGame = useCallback(() => {
    setCurrentGameIndex((i) => Math.min(games.length - 1, i + 1));
  }, [games.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goFirst();
      } else if (e.key === 'End') {
        e.preventDefault();
        goLast();
      }
    },
    [goFirst, goPrev, goNext, goLast]
  );

  // Custom square styles for last move highlight
  const customSquareStyles = useMemo(() => {
    if (!lastMove) return {};
    const highlight = 'rgba(255, 170, 0, 0.35)';
    return {
      [lastMove.from]: { backgroundColor: highlight },
      [lastMove.to]: { backgroundColor: highlight },
    };
  }, [lastMove]);

  // Player names
  const whiteName = currentGame?.whiteName || (currentGame?.pgn ? extractTag(currentGame.pgn, 'White') : '') || 'White';
  const blackName = currentGame?.blackName || (currentGame?.pgn ? extractTag(currentGame.pgn, 'Black') : '') || 'Black';
  const displayWhite = whiteName === '?' ? 'White' : whiteName;
  const displayBlack = blackName === '?' ? 'Black' : blackName;

  const orientation = currentGame?.orientation || 'white';

  // --- Empty state ---
  if (games.length === 0) {
    return (
      <div style={{ background: styles.sectionBackground }}>
        <div style={{ padding: '60px 24px', textAlign: 'center', color: styles.mutedColor }}>
          <Trophy size={48} style={{ marginBottom: 16, opacity: 0.4, margin: '0 auto 16px' }} />
          <p>No games added yet</p>
          <FavouriteGamesStyles accent={safeAccent} />
        </div>
      </div>
    );
  }

  // --- Build move list pairs ---
  const movePairs = [];
  for (let i = 0; i < moveHistory.length; i += 2) {
    const moveNum = Math.floor(i / 2) + 1;
    movePairs.push({
      num: moveNum,
      white: { san: moveHistory[i], index: i + 1 },
      black: moveHistory[i + 1]
        ? { san: moveHistory[i + 1], index: i + 2 }
        : null,
    });
  }

  return (
    <div
      className="fg-outer"
      style={{ background: styles.sectionBackground }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="fg-container">
        <div className="fg-layout">
          {/* LEFT COLUMN: Info panel */}
          <div className="fg-left">
            <h2 className="fg-title fg-title-desktop" style={{ color: styles.headingColor }}>
              {content.title || 'My Favourite Games'}
            </h2>

            {/* Player names card */}
            <div className="fg-info-card">
              <div className="fg-players">
                <div className="fg-player">
                  <div className="fg-player-dot white" />
                  <span className="fg-player-name" style={{ color: styles.bodyColor }}>
                    {displayWhite}
                  </span>
                </div>
                <span className="fg-vs" style={{ color: styles.mutedColor }}>vs</span>
                <div className="fg-player">
                  <div className="fg-player-dot black" />
                  <span className="fg-player-name" style={{ color: styles.bodyColor }}>
                    {displayBlack}
                  </span>
                </div>
              </div>

              {/* Game label */}
              {currentGame?.label && (
                <div className="fg-game-label" style={{ color: styles.mutedColor }}>
                  {currentGame.label}
                </div>
              )}

              {/* Game navigation */}
              {games.length > 1 && (
                <div className="fg-game-nav">
                  <button
                    className="fg-nav-btn"
                    onClick={prevGame}
                    disabled={currentGameIndex === 0}
                    style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                    title="Previous game"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="fg-game-indicator" style={{ color: styles.mutedColor }}>
                    Game {currentGameIndex + 1} of {games.length}
                  </span>
                  <button
                    className="fg-nav-btn"
                    onClick={nextGame}
                    disabled={currentGameIndex === games.length - 1}
                    style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                    title="Next game"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Move list */}
            <div className="fg-moves-card">
              <div className="fg-moves-scroll">
                {movePairs.map((pair) => (
                  <div key={pair.num} className="fg-move-row">
                    <span className="fg-move-num" style={{ color: styles.mutedColor }}>
                      {pair.num}.
                    </span>
                    <span
                      className={`fg-move ${currentMoveIndex === pair.white.index ? 'active' : ''}`}
                      onClick={() => setCurrentMoveIndex(pair.white.index)}
                      style={{
                        color: styles.bodyColor,
                        ...(currentMoveIndex === pair.white.index
                          ? { background: safeAccent + '33', color: safeAccent }
                          : {}),
                      }}
                    >
                      {pair.white.san}
                    </span>
                    {pair.black && (
                      <span
                        className={`fg-move ${currentMoveIndex === pair.black.index ? 'active' : ''}`}
                        onClick={() => setCurrentMoveIndex(pair.black.index)}
                        style={{
                          color: styles.bodyColor,
                          ...(currentMoveIndex === pair.black.index
                            ? { background: safeAccent + '33', color: safeAccent }
                            : {}),
                        }}
                      >
                        {pair.black.san}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Move navigation buttons */}
            <div className="fg-move-nav">
              <button
                className="fg-move-btn"
                onClick={goFirst}
                disabled={currentMoveIndex === 0}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                title="First move"
              >
                <ChevronsLeft size={18} />
              </button>
              <button
                className="fg-move-btn"
                onClick={goPrev}
                disabled={currentMoveIndex === 0}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                title="Previous move"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                className="fg-move-btn"
                onClick={goNext}
                disabled={currentMoveIndex >= moveHistory.length}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                title="Next move"
              >
                <ChevronRight size={18} />
              </button>
              <button
                className="fg-move-btn"
                onClick={goLast}
                disabled={currentMoveIndex >= moveHistory.length}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                title="Last move"
              >
                <ChevronsRight size={18} />
              </button>
              <button
                className="fg-move-btn"
                onClick={goFirst}
                disabled={currentMoveIndex === 0}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                title="Reset to start"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: Board */}
          <div className="fg-right">
            <h2 className="fg-title fg-title-mobile" style={{ color: styles.headingColor }}>
              {content.title || 'My Favourite Games'}
            </h2>
            <div className="fg-board-area">
              <Chessboard
                position={currentFen}
                boardOrientation={orientation}
                arePiecesDraggable={false}
                areArrowsAllowed={false}
                customSquareStyles={customSquareStyles}
                animationDuration={200}
                customLightSquareStyle={{ backgroundColor: '#eeeed2' }}
                customDarkSquareStyle={{
                  backgroundColor: matchTheme ? safeAccent : '#769656',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <FavouriteGamesStyles accent={safeAccent} />
    </div>
  );
};

const FavouriteGamesStyles = ({ accent }) => (
  <style>{`
    .fg-outer {
      width: 100%;
      outline: none;
    }

    .fg-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 24px;
    }

    .fg-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
      align-items: center;
    }

    /* LEFT COLUMN */
    .fg-left {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .fg-title {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 700;
      text-align: center;
    }

    .fg-title-mobile {
      display: none;
    }

    .fg-title-desktop {
      display: block;
    }

    /* Info card */
    .fg-info-card {
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      padding: 20px 24px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .fg-players {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .fg-player {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .fg-player-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .fg-player-dot.white {
      background: #ffffff;
      box-shadow: 0 0 0 1.5px rgba(0, 0, 0, 0.15);
    }

    .fg-player-dot.black {
      background: #1a1a1a;
      box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.15);
    }

    .fg-player-name {
      font-size: 1rem;
      font-weight: 600;
    }

    .fg-vs {
      font-size: 0.8rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .fg-game-label {
      font-size: 0.85rem;
      font-style: italic;
    }

    /* Game navigation */
    .fg-game-nav {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .fg-nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: 1px solid;
      background: transparent;
      cursor: pointer;
      transition: all 0.15s;
      font-family: inherit;
    }

    .fg-nav-btn:hover:not(:disabled) {
      border-color: ${accent};
      color: ${accent};
    }

    .fg-nav-btn:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .fg-game-indicator {
      font-size: 0.8rem;
      font-weight: 600;
      white-space: nowrap;
    }

    /* Moves card */
    .fg-moves-card {
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      padding: 12px 16px;
    }

    .fg-moves-scroll {
      max-height: 260px;
      overflow-y: auto;
      display: flex;
      flex-wrap: wrap;
      gap: 2px 0;
      align-content: flex-start;
    }

    .fg-moves-scroll::-webkit-scrollbar {
      width: 4px;
    }

    .fg-moves-scroll::-webkit-scrollbar-track {
      background: transparent;
    }

    .fg-moves-scroll::-webkit-scrollbar-thumb {
      background: rgba(128, 128, 128, 0.3);
      border-radius: 2px;
    }

    .fg-move-row {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      margin-right: 6px;
    }

    .fg-move-num {
      font-size: 0.8rem;
      font-weight: 600;
      min-width: 24px;
      text-align: right;
      margin-right: 2px;
      font-family: 'Monaco', 'Menlo', monospace;
      user-select: none;
    }

    .fg-move {
      font-size: 0.85rem;
      font-family: 'Monaco', 'Menlo', monospace;
      padding: 2px 6px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.1s;
      user-select: none;
    }

    .fg-move:hover {
      background: rgba(128, 128, 128, 0.15);
    }

    .fg-move.active {
      font-weight: 700;
    }

    /* Move navigation */
    .fg-move-nav {
      display: flex;
      gap: 6px;
      justify-content: center;
    }

    .fg-move-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 36px;
      border-radius: 8px;
      border: 1px solid;
      background: transparent;
      cursor: pointer;
      transition: all 0.15s;
      font-family: inherit;
    }

    .fg-move-btn:hover:not(:disabled) {
      border-color: ${accent};
      color: ${accent};
    }

    .fg-move-btn:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    /* RIGHT COLUMN */
    .fg-right {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .fg-board-area {
      width: 100%;
      aspect-ratio: 1;
      max-width: 450px;
      overflow: hidden;
    }

    /* Mobile */
    @media (max-width: 768px) {
      .fg-layout {
        grid-template-columns: 1fr;
        gap: 24px;
      }

      .fg-left {
        order: 2;
      }

      .fg-right {
        order: 1;
      }

      .fg-title-mobile {
        display: block;
      }

      .fg-title-desktop {
        display: none;
      }

      .fg-container {
        padding: 24px 16px;
      }

      .fg-title {
        font-size: 1.5rem;
      }

      .fg-board-area {
        max-width: 400px;
      }

      .fg-move-btn {
        width: 36px;
        height: 32px;
      }
    }

    @media (max-width: 480px) {
      .fg-title {
        font-size: 1.3rem;
      }

      .fg-board-area {
        max-width: calc(100vw - 48px);
      }
    }
  `}</style>
);

export default FavouriteGamesRenderer;
